import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  useColorScheme,
  StyleSheet,
  Dimensions,
  Modal,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
// 导入 ECharts 相关组件，替换原有的 LineChart
import WebECharts from '../components/WebECharts';
import ECharts from '../components/ECharts';

const screenWidth = Dimensions.get('window').width;
// 根据平台选择合适的图表组件
const ChartComponent = Platform.OS === 'web' ? WebECharts : ECharts;

const HealthDataScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('weight');
  const [showAddDataModal, setShowAddDataModal] = useState(false);
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  
  // 模拟数据 - 改为最近7天的数据
  const [healthData, setHealthData] = useState({
    weight: [],
    height: [],
  });
  
  // 生成最近7天的数据
  useEffect(() => {
    // 生成过去7天的体重数据
    const generateLast7DaysWeightData = () => {
      // 起始体重：70.5kg
      let startWeight = 70.5;
      // 每天平均减轻体重(kg)
      const dailyWeightLoss = 0.5 / 7;
      
      const weightData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD 格式
        
        // 添加一些随机变化，但保持整体下降趋势
        const randomVariation = (Math.random() * 0.2 - 0.1); // -0.1kg ~ 0.1kg
        const weightValue = startWeight - (6 - i) * dailyWeightLoss + randomVariation;
        
        weightData.push({
          date: dateStr,
          value: parseFloat(weightValue.toFixed(1))
        });
      }
      
      return weightData;
    };
    
    // 生成过去7天的身高数据 (身高通常不会每天变化，所以值保持一致)
    const generateLast7DaysHeightData = () => {
      const heightData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD 格式
        
        // 身高保持不变
        heightData.push({
          date: dateStr,
          value: 175
        });
      }
      
      return heightData;
    };
    
    setHealthData({
      weight: generateLast7DaysWeightData(),
      height: generateLast7DaysHeightData()
    });
  }, []);

  // 计算BMI
  const calculateBMI = () => {
    if (healthData.weight.length === 0 || healthData.height.length === 0) return "未知";
    
    const latestWeight = healthData.weight[healthData.weight.length - 1].value;
    const latestHeight = healthData.height[healthData.height.length - 1].value / 100; // 转换为米
    
    const bmi = latestWeight / (latestHeight * latestHeight);
    return bmi.toFixed(1);
  };

  // 获取BMI状态
  const getBMIStatus = (bmi) => {
    if (bmi === "未知") return { status: "未知", color: "#9ca3af" };
    
    const numBMI = parseFloat(bmi);
    
    if (numBMI < 18.5) return { status: "偏瘦", color: "#60a5fa" };
    if (numBMI < 24) return { status: "正常", color: "#34d399" };
    if (numBMI < 28) return { status: "超重", color: "#fbbf24" };
    return { status: "肥胖", color: "#ef4444" };
  };

  // 添加新数据
  const addNewData = () => {
    const today = new Date().toISOString().split('T')[0];
    
    if (activeTab === 'weight' && weight) {
      const weightValue = parseFloat(weight);
      if (isNaN(weightValue) || weightValue <= 0 || weightValue > 300) {
        Alert.alert('错误', '请输入有效的体重数值(0-300kg)');
        return;
      }
      
      setHealthData({
        ...healthData,
        weight: [...healthData.weight, { date: today, value: weightValue }]
      });
      
      setWeight('');
      setShowAddDataModal(false);
    } 
    else if (activeTab === 'height' && height) {
      const heightValue = parseFloat(height);
      if (isNaN(heightValue) || heightValue <= 0 || heightValue > 250) {
        Alert.alert('错误', '请输入有效的身高数值(0-250cm)');
        return;
      }
      
      setHealthData({
        ...healthData,
        height: [...healthData.height, { date: today, value: heightValue }]
      });
      
      setHeight('');
      setShowAddDataModal(false);
    }
  };

  // 准备体重图表数据 - ECharts格式
  const getWeightChartDataECharts = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          // 从原始数据中获取完整日期
          const fullDate = healthData.weight[params[0].dataIndex].date;
          let tooltip = `${fullDate}<br/>`;
          params.forEach(param => {
            tooltip += param.marker + ' ' + param.seriesName + ': ' + param.value + ' kg<br/>';
          });
          return tooltip;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        // 优化日期显示：只显示月-日
        data: healthData.weight.map(item => {
          const date = new Date(item.date);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: '体重(kg)',
        data: healthData.weight.map(item => item.value),
        type: 'line',
        smooth: true,
        symbolSize: 6,
        itemStyle: {
          color: '#2563eb'
        },
        lineStyle: {
          width: 3,
          color: '#2563eb'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: '#2563ebCC' // 带透明度的颜色
            }, {
              offset: 1,
              color: '#2563eb11'
            }]
          }
        }
      }]
    };
  };
  
  // 准备身高图表数据 - ECharts格式
  const getHeightChartDataECharts = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          // 从原始数据中获取完整日期
          const fullDate = healthData.height[params[0].dataIndex].date;
          let tooltip = `${fullDate}<br/>`;
          params.forEach(param => {
            tooltip += param.marker + ' ' + param.seriesName + ': ' + param.value + ' cm<br/>';
          });
          return tooltip;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        // 优化日期显示：只显示月-日
        data: healthData.height.map(item => {
          const date = new Date(item.date);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: '身高(cm)',
        data: healthData.height.map(item => item.value),
        type: 'line',
        smooth: true,
        symbolSize: 6,
        itemStyle: {
          color: '#2563eb'
        },
        lineStyle: {
          width: 3,
          color: '#2563eb'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: '#2563ebCC' // 带透明度的颜色
            }, {
              offset: 1,
              color: '#2563eb11'
            }]
          }
        }
      }]
    };
  };

  const bmi = calculateBMI();
  const bmiStatus = getBMIStatus(bmi);

  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 健康仪表盘 */}
      <View style={{
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: isDarkMode ? 'white' : '#1f2937',
        }}>
          健康仪表盘
        </Text>

        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          {/* 最新体重 */}
          <View style={{
            flex: 1,
            alignItems: 'center',
            padding: 12,
            backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
            borderRadius: 8,
            marginRight: 8,
          }}>
            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
              marginBottom: 4,
            }}>
              当前体重
            </Text>
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {healthData.weight.length > 0 ? 
                `${healthData.weight[healthData.weight.length - 1].value} kg` : 
                '未记录'}
            </Text>
          </View>

          {/* 最新身高 */}
          <View style={{
            flex: 1,
            alignItems: 'center',
            padding: 12,
            backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
            borderRadius: 8,
            marginLeft: 8,
          }}>
            <Text style={{
              fontSize: 14,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
              marginBottom: 4,
            }}>
              当前身高
            </Text>
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {healthData.height.length > 0 ? 
                `${healthData.height[healthData.height.length - 1].value} cm` : 
                '未记录'}
            </Text>
          </View>
        </View>

        {/* BMI指数 */}
        <View style={{
          alignItems: 'center',
          padding: 16,
          backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
          borderRadius: 8,
          marginBottom: 16,
        }}>
          <Text style={{
            fontSize: 16,
            color: isDarkMode ? '#e0e0e0' : '#4b5563',
            marginBottom: 8,
          }}>
            BMI指数
          </Text>
          <Text style={{
            fontSize: 28,
            fontWeight: 'bold',
            color: bmiStatus.color,
            marginBottom: 4,
          }}>
            {bmi}
          </Text>
          <Text style={{
            fontSize: 16,
            color: bmiStatus.color,
          }}>
            {bmiStatus.status}
          </Text>
        </View>

        {/* BMI参考表 */}
        <View style={{
          padding: 12,
          backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
          borderRadius: 8,
        }}>
          <Text style={{
            fontSize: 14,
            fontWeight: 'bold',
            color: isDarkMode ? 'white' : '#1f2937',
            marginBottom: 8,
          }}>
            BMI参考值
          </Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ fontSize: 12, color: '#60a5fa' }}>偏瘦: &lt;18.5</Text>
            <Text style={{ fontSize: 12, color: '#34d399' }}>正常: 18.5-23.9</Text>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 12, color: '#fbbf24' }}>超重: 24-27.9</Text>
            <Text style={{ fontSize: 12, color: '#ef4444' }}>肥胖: ≥28</Text>
          </View>
        </View>
      </View>

      {/* 数据记录标签页 */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
      }}>
        <TouchableOpacity
          onPress={() => setActiveTab('weight')}
          style={{
            flex: 1,
            paddingVertical: 12,
            backgroundColor: activeTab === 'weight' ? '#2563eb' : (isDarkMode ? '#8295b7' : '#f3f4f6'),
            borderRadius: 8,
            marginRight: 8,
            alignItems: 'center',
          }}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'weight' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
          }}>
            体重记录
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          onPress={() => setActiveTab('height')}
          style={{
            flex: 1,
            paddingVertical: 12,
            backgroundColor: activeTab === 'height' ? '#2563eb' : (isDarkMode ? '#8295b7' : '#f3f4f6'),
            borderRadius: 8,
            marginLeft: 8,
            alignItems: 'center',
          }}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'height' ? 'white' : (isDarkMode ? '#e0e0e0' : '#4b5563'),
          }}>
            身高记录
          </Text>
        </TouchableOpacity>
      </View>

      {/* 数据图表 */}
      <View style={{
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
      }}>
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
          <Text style={{
            fontSize: 18,
            fontWeight: 'bold',
            color: isDarkMode ? 'white' : '#1f2937',
          }}>
            {activeTab === 'weight' ? '体重趋势' : '身高趋势'}
          </Text>
          
          <TouchableOpacity
            onPress={() => setShowAddDataModal(true)}
            style={{
              backgroundColor: '#2563eb',
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 6,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Ionicons name="add" size={16} color="white" />
            <Text style={{ color: 'white', fontWeight: '500', marginLeft: 4 }}>
              添加记录
            </Text>
          </TouchableOpacity>
        </View>

        {/* 图表 */}
        {(activeTab === 'weight' && healthData.weight.length > 1) || (activeTab === 'height' && healthData.height.length > 1) ? (
          <View style={{
            marginVertical: 8,
            borderRadius: 16,
            alignItems: 'center',
            backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
            padding: 8,
          }}>
            <ChartComponent
              option={activeTab === 'weight' ? getWeightChartDataECharts() : getHeightChartDataECharts()}
              height={220}
              width={screenWidth - 64}
            />
          </View>
        ) : (
          <View style={{
            height: 220,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
            borderRadius: 16,
          }}>
            <Text style={{
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
              textAlign: 'center',
            }}>
              {activeTab === 'weight' ? '需要至少两条体重记录才能生成图表' : '需要至少两条身高记录才能生成图表'}
            </Text>
            <TouchableOpacity
              onPress={() => setShowAddDataModal(true)}
              style={{
                marginTop: 12,
                paddingHorizontal: 16,
                paddingVertical: 8,
                backgroundColor: '#2563eb',
                borderRadius: 8,
              }}
            >
              <Text style={{ color: 'white' }}>立即添加</Text>
            </TouchableOpacity>
          </View>
        )}
        
        {/* 数据列表 */}
        <View style={{ marginTop: 16 }}>
          <Text style={{
            fontSize: 16,
            fontWeight: 'bold',
            color: isDarkMode ? 'white' : '#1f2937',
            marginBottom: 12,
          }}>
            历史记录
          </Text>
          
          {activeTab === 'weight' ? (
            healthData.weight.slice().reverse().map((item, index) => (
              <View 
                key={index}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingVertical: 10,
                  borderBottomWidth: index !== healthData.weight.length - 1 ? 1 : 0,
                  borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
                }}
              >
                <Text style={{ color: isDarkMode ? '#e0e0e0' : '#4b5563' }}>
                  {item.date}
                </Text>
                <Text style={{ 
                  fontWeight: '500',
                  color: isDarkMode ? 'white' : '#1f2937',
                }}>
                  {item.value} kg
                </Text>
              </View>
            ))
          ) : (
            healthData.height.slice().reverse().map((item, index) => (
              <View 
                key={index}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingVertical: 10,
                  borderBottomWidth: index !== healthData.height.length - 1 ? 1 : 0,
                  borderBottomColor: isDarkMode ? '#84929e' : '#f0f0f0',
                }}
              >
                <Text style={{ color: isDarkMode ? '#e0e0e0' : '#4b5563' }}>
                  {item.date}
                </Text>
                <Text style={{ 
                  fontWeight: '500',
                  color: isDarkMode ? 'white' : '#1f2937',
                }}>
                  {item.value} cm
                </Text>
              </View>
            ))
          )}
        </View>
      </View>

      {/* 添加数据模态框 */}
      <Modal
        visible={showAddDataModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowAddDataModal(false)}
      >
        <View style={{
          flex: 1,
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
          <View style={{
            padding: 16,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : 'white',
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              {activeTab === 'weight' ? '添加体重记录' : '添加身高记录'}
            </Text>
            
            <View style={{ marginBottom: 16 }}>
              <Text style={{
                marginBottom: 8,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
              }}>
                {activeTab === 'weight' ? '体重 (kg)' : '身高 (cm)'}
              </Text>
              <TextInput
                value={activeTab === 'weight' ? weight : height}
                onChangeText={activeTab === 'weight' ? setWeight : setHeight}
                keyboardType="numeric"
                placeholder={activeTab === 'weight' ? '请输入体重' : '请输入身高'}
                placeholderTextColor={isDarkMode ? '#8295b7' : '#9ca3af'}
                style={{
                  padding: 12,
                  borderWidth: 1,
                  borderColor: isDarkMode ? '#84929e' : '#e5e7eb',
                  borderRadius: 8,
                  color: isDarkMode ? 'white' : '#1f2937',
                  backgroundColor: isDarkMode ? '#8295b7' : 'white',
                }}
              />
            </View>
            
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <TouchableOpacity
                onPress={() => setShowAddDataModal(false)}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  backgroundColor: isDarkMode ? '#657084' : '#f3f4f6',
                  borderRadius: 8,
                  marginRight: 8,
                }}
              >
                <Text style={{
                  textAlign: 'center',
                  fontWeight: '600',
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  取消
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={addNewData}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  backgroundColor: '#2563eb',
                  borderRadius: 8,
                  marginLeft: 8,
                }}
              >
                <Text style={{
                  textAlign: 'center',
                  fontWeight: '600',
                  color: 'white',
                }}>
                  保存
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

export default HealthDataScreen; 